import {useContext} from 'react';
import {useTranslation} from 'react-i18next';
import Anchor from '../../../common/component/Anchor';
import DescText from '../../../common/component/DescText';
import {pAlert} from '../../../common/component/PAlert';
import Row from '../../../common/component/Row';
import Text from '../../../common/component/Text';
import TextButton from '../../../common/component/TextButton';
import {Page} from '../../../common/constant/page';
import {ButtonSet} from '../../../common/type/alert';
import {getRemainText} from '../../../common/util/date';
import {formatNumber} from '../../../common/util/number';
import {routeTo} from '../../../common/util/PageRouter';
import {sendEvent} from '../../../common/util/tracking';
import DashedInfo from '../../../commonSub/component/DashedInfo';
import {ContentPageContext, TRACKING_PAGE_NAME} from '../../common';
import {IContentTicketType, IContentTicketInfo} from '../../type';

interface IProps {
  ticketInfo: IContentTicketInfo;
  contentTicketType: IContentTicketType;
  sid: string;
  id: number;
}

export default function ContentTicketInfoV4({
  ticketInfo,
  sid,
  id,
  contentTicketType,
}: IProps) {
  const {t} = useTranslation();
  const {
    totalTicketCount,
    waitfree,
    giftCount,
    rentCount,
    buyCount,
    canPurchaseTicket,
  } = ticketInfo;
  const {
    waitfreeCount = 0,
    waitfreeTimeUnit,
    excludedEpisodeCount = 0,
    hasWaitfreeTicket,
    chargeAtTs,
    nowFromServer = 0,
  } = waitfree || {};

  const isContentTicketTab = contentTicketType === 'contentTicketTab';
  const {isStopContent} = useContext(ContentPageContext);
  const {
    wrapperClassName,
    btnClassName,
    btnTextClassName,
    waitfreeTextClassName,
    ticketAmountTextClassName,
    dashedInfoDescClassName,
    dashedInfoTitleClassName,
    dashedClassName,
  } = CONTENT_TICKET_INFO_CN_MAP[contentTicketType];

  const onClickPurchase = () => {
    sendEvent({
      section: 'content_home',
      page: isContentTicketTab
        ? TRACKING_PAGE_NAME.ticket
        : 'content_ticketpurchase_history',
      actionName: 'ticket_purchase',
      action: 'click',
      customProps: {
        page_content_id: id,
      },
    });
    routeTo({page: Page.ContentPurchase, sid, id});
  };

  const handleClickCancel = async () => {
    sendEvent({
      section: 'content_home',
      page: isContentTicketTab
        ? TRACKING_PAGE_NAME.ticket
        : 'content_ticketpurchase_history',
      actionName: 'content_ticket_cancel',
      action: 'click',
      customProps: {
        page_content_id: id,
      },
    });
    const {okClicked} = await pAlert({
      title: t('contenthome_stop_sell_refund_popup'),
      buttonSet: ButtonSet.CancelAndConfirm,
    });
    okClicked &&
      routeTo({
        page: Page.ContentTicketHistory,
        id,
        sid,
      });
  };

  return (
    <div className={cn('rounded-12', wrapperClassName)}>
      {isContentTicketTab && (
        <Text className="s16-medium-white leading-24 mb-7">
          {t('contenthome_ticket_tab_gift_ticket_remaining')}
        </Text>
      )}
      <Row
        rightElement={
          <>
            {canPurchaseTicket && (
              <Anchor onClick={onClickPurchase}>
                <TextButton
                  textClassName={btnTextClassName}
                  className={cn('px-12', btnClassName)}
                  height="28px"
                >
                  {t('common_purchase')}
                </TextButton>
              </Anchor>
            )}
            {isContentTicketTab &&
              !canPurchaseTicket &&
              isStopContent &&
              totalTicketCount > 0 && (
                <TextButton
                  onClick={handleClickCancel}
                  textClassName="s12-medium-black"
                  className="px-12 btn-white"
                  height="28px"
                >
                  {t('common_cancel')}
                </TextButton>
              )}
          </>
        }
      >
        <Text className={cn('s32-regular-white', ticketAmountTextClassName)}>
          {t('common_ticket_amount', {count: totalTicketCount})}
        </Text>
      </Row>
      <div>
        {waitfree && waitfreeTimeUnit && (
          <DashedInfo
            className="mt-18"
            titleElement={
              <Anchor
                onClick={() => {
                  sendEvent({
                    section: 'content_home',
                    page: 'content_ticketpurchase_history',
                    actionName: 'gidamu_info',
                    action: 'click',
                    customProps: {
                      page_content_id: id,
                    },
                  });
                  pAlert({
                    title: t(WAITFREE_TEXT_KEY_MAP[waitfreeTimeUnit], {
                      count: waitfreeCount,
                    }),
                    desc: (
                      <>
                        <DescText className="s13-regular-grey08 mt-10">
                          {t(WAITFREE_POPUP_TEXT_KEY_MAP[waitfreeTimeUnit], {
                            count: waitfreeCount,
                          })}
                        </DescText>
                        <DescText className="s13-regular-grey08 mt-10">
                          {t('contenthome_tickethistory_popup_detail_2', {
                            value: formatNumber(excludedEpisodeCount),
                          })}
                        </DescText>
                        <DescText className="s13-regular-grey08 mt-10">
                          {t('contenthome_tickethistory_popup_detail_3', {
                            value: formatNumber(waitfreeCount),
                          })}
                        </DescText>
                      </>
                    ),
                    isTextAlignCenter: false,
                  });
                }}
              >
                <Text className={cn(waitfreeTextClassName, 'underline')}>
                  {t('common_ticket_gidamu')}
                </Text>
              </Anchor>
            }
            titleClassName={dashedInfoTitleClassName}
            desc={
              hasWaitfreeTicket
                ? t('common_ticket_amount', {count: 1})
                : getRemainText(
                    {
                      endDate: chargeAtTs!,
                      now: nowFromServer,
                    },
                    t
                  )
            }
            descClassName={dashedInfoDescClassName}
            dashedClassName={cn(dashedClassName, 'mx-10')}
          />
        )}
        {giftCount > 0 && (
          <DashedInfo
            className="mt-10 first:mt-18"
            title={t('common_ticket_gift')}
            titleClassName={dashedInfoTitleClassName}
            desc={t('common_ticket_amount', {count: giftCount})}
            descClassName={dashedInfoDescClassName}
            dashedClassName={cn(dashedClassName, 'mx-10')}
          />
        )}
        {typeof rentCount === 'number' && (
          <DashedInfo
            className="mt-10 first:mt-18"
            title={t('common_ticket_rental')}
            titleClassName={dashedInfoTitleClassName}
            desc={t('common_ticket_amount', {count: rentCount})}
            descClassName={dashedInfoDescClassName}
            dashedClassName={cn(dashedClassName, 'mx-10')}
          />
        )}
        {typeof buyCount === 'number' && (
          <DashedInfo
            className="mt-10 first:mt-18"
            title={t('common_ticket_possession')}
            titleClassName={dashedInfoTitleClassName}
            desc={t('common_ticket_amount', {count: buyCount})}
            descClassName={dashedInfoDescClassName}
            dashedClassName={cn(dashedClassName, 'mx-10')}
          />
        )}
      </div>
    </div>
  );
}

const WAITFREE_TEXT_KEY_MAP = {
  day: 'contenthome_tickethistory_gidamu_day',
  hour: 'contenthome_tickethistory_gidamu_hour',
  minute: 'contenthome_tickethistory_gidamu_minute',
};

const WAITFREE_POPUP_TEXT_KEY_MAP = {
  day: 'contenthome_tickethistory_popup_detail_day_1',
  hour: 'contenthome_tickethistory_popup_detail_hour_1',
  minute: 'contenthome_tickethistory_popup_detail_minute_1',
};

const CONTENT_TICKET_INFO_CN_MAP: {
  [key in IContentTicketType]: {
    wrapperClassName: string;
    btnTextClassName: string;
    btnClassName: string;
    waitfreeTextClassName: string;
    ticketAmountTextClassName?: string;
    dashedInfoDescClassName: string;
    dashedInfoTitleClassName: string;
    dashedClassName: string;
  };
} = {
  contentTicketTab: {
    wrapperClassName: 'bg-transparent',
    btnTextClassName: 's12-medium-black',
    btnClassName: 'btn-white flex-center',
    waitfreeTextClassName: 's13-regular-white opacity-85',
    dashedInfoDescClassName: 's13-medium-white',
    dashedInfoTitleClassName: 's13-regular-white opacity-85',
    dashedClassName: 'border-white opacity-15',
  },
  contentTicketHistory: {
    wrapperClassName: 'bg-grey-01 light:bg-white px-18 py-24',
    btnTextClassName: 's12-medium-white',
    btnClassName: 'btn-purple flex-center',
    waitfreeTextClassName: 's13-regular-white opacity-85 light:text-black',
    ticketAmountTextClassName: 'light:text-black',
    dashedInfoDescClassName: 's13-medium-white light:text-black',
    dashedInfoTitleClassName: 's13-regular-white opacity-85 light:text-black',
    dashedClassName: 'border-grey-03',
  },
};
